import { orderBy } from 'lodash';
import {
  BaseImageList,
  ExternalLink,
  FileLink,
  ImageList,
  Panel,
  Tabs,
  TruncatedText,
  Typography,
} from 'melp-design/components';
import { Colors } from 'melp-design/style';
import { Box, Link, Paper, Stack } from '@mui/material';
import { FC, Fragment, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useCompanyLanguages } from 'state/Administrators';
import {
  Survey,
  SurveyResults,
  surveyChoiceAnswerTypes,
  surveyRatingScaleAnswerTypes,
} from 'store/surveys';
import { formatLanguage } from 'utils/format';
import { resolveTranslation } from 'utils/general';
import { useExportSurveyResultsJob } from '../atoms/survey-results-export';
import { parsedQuestionResults } from '../surveys.utils';

type Language = Survey['translations'][number]['language'];

interface PostedMediaProps {
  description?: string;
  imageUrl?: string | null;
  imageId?: string | null;
  attachments?: Array<{ id: string; url: string; name: string }>;
  videos?: string[];
}

const PostedMedia: FC<PostedMediaProps> = ({
  description,
  imageUrl,
  imageId,
  attachments = [],
  videos = [],
}) => {
  const visibleAttachments = attachments.filter(
    (attachment) => attachment.url || attachment.id,
  );
  const visibleVideos = videos.filter(Boolean);

  if (
    !description &&
    !imageUrl &&
    !imageId &&
    !visibleAttachments.length &&
    !visibleVideos.length
  ) {
    return null;
  }

  return (
    <Stack gap={2}>
      {description ? (
        <TruncatedText
          text={description}
          maxLength={100}
          textProps={{ color: 'textSecondary' }}
        />
      ) : null}

      {imageUrl ? (
        <BaseImageList items={[{ src: imageUrl }]} imageFit="full" cols={2} />
      ) : imageId ? (
        <ImageList items={[{ imageId }]} imageFit="full" cols={2} />
      ) : null}

      {visibleVideos.length ? (
        <Stack>
          {visibleVideos.map((video) => (
            <ExternalLink key={video} label={video} href={video} />
          ))}
        </Stack>
      ) : null}

      {visibleAttachments.length ? (
        <Stack>
          {visibleAttachments.map((attachment) =>
            attachment.url ? (
              <ExternalLink
                key={attachment.id}
                label={`📎 ${attachment.name}`}
                href={attachment.url}
              />
            ) : (
              <FileLink key={attachment.id} id={attachment.id} />
            ),
          )}
        </Stack>
      ) : null}
    </Stack>
  );
};

interface Props {
  survey: Survey;
  results: SurveyResults;
  surveyId: string;
}

export const SurveyContentSummary: FC<Props> = ({
  survey,
  results,
  surveyId,
}) => {
  const { t, i18n } = useTranslation();
  const { defaultLanguage } = useCompanyLanguages();
  const { exportSurveyResults, isExporting } = useExportSurveyResultsJob();
  const [selectedLanguage, setSelectedLanguage] = useState<Language | null>(
    null,
  );

  const languages = orderBy(
    [
      ...new Set(
        survey.translations.map((translation) => translation.language),
      ),
    ],
    [(language) => language === defaultLanguage],
    ['desc'],
  );
  const activeLanguage =
    (selectedLanguage && languages.includes(selectedLanguage)
      ? selectedLanguage
      : undefined) ??
    languages.find((language) => language === i18n.language.toUpperCase()) ??
    languages.find((language) => language === defaultLanguage) ??
    languages[0];

  const intro = activeLanguage
    ? resolveTranslation(survey.translations, activeLanguage, defaultLanguage)
    : null;

  const parsedById = new Map(
    parsedQuestionResults({
      results,
      userLng: activeLanguage ?? i18n.language,
      defaultLng: defaultLanguage,
    }).map((question) => [question.id, question]),
  );

  const sourceQuestions = survey.questions.length
    ? survey.questions
    : results.questions;
  const pages = [...sourceQuestions].sort((a, b) => a.index - b.index);

  return (
    <Panel title={t('benefitTemplates.templateContent')}>
      <Stack gap={2}>
        {languages.length > 1 && activeLanguage ? (
          <Tabs
            value={activeLanguage}
            items={languages.map((language) => ({
              value: language,
              label: formatLanguage(i18n.language, language),
            }))}
            onChange={setSelectedLanguage}
          />
        ) : null}

        <Stack gap={4}>
          {intro ? (
            <Stack gap={2}>
              <Typography variant="h3">{intro.title || '-'}</Typography>
              <PostedMedia
                description={intro.description}
                imageUrl={intro.imageUrl}
                imageId={intro.imageId}
                attachments={intro.attachments}
                videos={intro.videos}
              />
            </Stack>
          ) : null}

          {pages.map((question, index) => {
            const content = resolveTranslation(
              question.translations,
              activeLanguage,
              defaultLanguage,
            );
            const parsed = parsedById.get(question.id);
            const title = `${index + 1}.  ${content?.text ?? ''}`;
            const imageUrl = content?.imageUrl || parsed?.imageUrl;
            const destinationPageByAnswerIndex = new Map(
              ('metadata' in question
                ? question.metadata.conditionalJumpRules
                : []
              ).flatMap((rule) => {
                const target = survey.questions[rule.questionIndex];
                const pageNumber = target
                  ? pages.findIndex((page) => page.id === target.id) + 1
                  : 0;

                return pageNumber > 0
                  ? [[rule.answerIndex, pageNumber] as const]
                  : [];
              }),
            );
            const pageHeading = (
              <Stack gap={0.5}>
                <Typography variant="h3">{title}</Typography>
                <Typography color="textSecondary">
                  {t(`surveys.answerType-${question.type}`)}
                </Typography>
                {'isRequired' in question &&
                question.type !== 'information_page' ? (
                  <Typography color="textSecondary">
                    {`${t('surveys.question_required')}: ${
                      question.isRequired ? t('common.yes') : t('common.no')
                    }`}
                  </Typography>
                ) : null}
              </Stack>
            );

            const showsAnswerResults =
              surveyChoiceAnswerTypes.some((type) => type === question.type) ||
              surveyRatingScaleAnswerTypes.some(
                (type) => type === question.type,
              );

            return (
              <Stack key={question.id} gap={2}>
                {pageHeading}
                <PostedMedia
                  description={content?.description}
                  imageUrl={imageUrl}
                  imageId={imageUrl ? null : content?.imageId}
                  attachments={content?.attachments}
                  videos={content?.videos}
                />
                {question.type === 'text_response' ? (
                  <Paper
                    sx={{
                      backgroundColor: Colors.lightBackgroundGrey,
                      padding: '15px',
                    }}
                  >
                    <Typography variant="p1">
                      <Trans
                        i18nKey="surveys.openQuestionsExplanation"
                        components={[
                          <Link
                            component="button"
                            color="primary"
                            onClick={() => {
                              exportSurveyResults({ surveyId });
                            }}
                            disabled={isExporting}
                            key="0"
                          />,
                        ]}
                      />
                    </Typography>
                  </Paper>
                ) : null}
                {showsAnswerResults ? (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns:
                        'minmax(0, 1fr) minmax(10rem, max-content) minmax(10rem, max-content)',
                      '& > *': {
                        py: 1.5,
                        borderBottom: `1px solid ${Colors.elementsGrey}`,
                      },
                      '& > :nth-child(3n + 2), & > :nth-child(3n)': {
                        pl: 2,
                        textAlign: 'right',
                      },
                    }}
                  >
                    <Box />
                    <Typography color="textSecondary">
                      {t('surveys.respondentsCount')}
                    </Typography>
                    <Typography color="textSecondary">
                      {t('surveys.respondentsPercentage')}
                    </Typography>
                    {(parsed?.answers ?? []).map((answer, i) => {
                      const destinationPage = destinationPageByAnswerIndex.get(
                        answer.index,
                      );

                      return (
                        <Fragment key={i}>
                          <Stack>
                            <Typography>{`${answer.isCorrect ? '✔' : ''} ${
                              answer.label
                            }`}</Typography>
                            {destinationPage ? (
                              <Typography color="textSecondary">
                                {t('surveys.conditional_jump.skips_to_page', {
                                  page: destinationPage,
                                })}
                              </Typography>
                            ) : null}
                            {answer.imageUrl ? (
                              <BaseImageList
                                items={[{ src: answer.imageUrl }]}
                                imageFit="full"
                                cols={3}
                              />
                            ) : null}
                          </Stack>
                          <Typography>{answer.responseCount}</Typography>
                          <Typography>{answer.responsePercents}</Typography>
                        </Fragment>
                      );
                    })}
                  </Box>
                ) : null}
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    </Panel>
  );
};
